import { useRef } from "react";
import { m, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLine, SectionLabel } from "./Reveal";
import { useDesktop } from "../hooks/useDesktop";
import { CHAPTERS, byTitle } from "../data/site";

export const Manifesto = () => {
  const ref = useRef(null);
  const isDesktop = useDesktop();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const rot = useTransform(scrollYProgress, [0, 1], [4, -4]);

  return (
    <section
      id="manifesto"
      ref={ref}
      data-testid="why-us-section"
      className="relative overflow-hidden bg-[#0d1219] py-24 lg:py-36"
    >
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10">
        <SectionLabel testId="manifesto-label">
          Chapter 02 · Why The Vault
        </SectionLabel>

        <h2 className="mt-8 max-w-4xl font-cond text-4xl font-extrabold uppercase leading-[0.92] sm:text-5xl lg:text-6xl">
          <MaskLine>Anyone can cut hair.</MaskLine>
          <MaskLine delay={0.1}>
            Very few can make you
            <span className="brass-text"> walk taller.</span>
          </MaskLine>
        </h2>

        <div className="mt-16 grid gap-14 lg:grid-cols-[1fr_0.85fr] lg:gap-24">
          <div>
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.08}>
                <article
                  data-testid={`chapter-${c.no}`}
                  className="group grid grid-cols-[auto_1fr] gap-6 border-t border-white/[0.08] py-9 transition-colors duration-500 hover:border-[#c5a059]/45"
                >
                  <span className="font-display text-sm text-[#c5a059]/70 transition-colors duration-500 group-hover:text-[#d8b46b]">
                    {c.no}
                  </span>
                  <div>
                    <h3 className="font-cond text-2xl font-bold uppercase tracking-wide text-[#f2ece0] sm:text-3xl">
                      {c.title}
                    </h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400">
                      {c.body}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {/* 3D parallax photo slab, desktop only */}
          <div className="perspective relative hidden self-start lg:block">
            <m.figure
              style={isDesktop ? { y, rotateY: rot } : undefined}
              className="sticky top-28 overflow-hidden border border-white/[0.08]"
            >
              <img
                src={byTitle("The old bank on 377").url}
                srcSet={byTitle("The old bank on 377").srcSet}
                sizes="(min-width: 1024px) 560px, 100vw"
                alt="The historic old bank building on US-377, Argyle TX · home of The Vault Barber Co."
                loading="lazy"
                decoding="async"
                width="400"
                height="500"
                className="aspect-[4/5] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d12]/90 via-transparent to-transparent" />
              <figcaption className="absolute inset-x-0 bottom-0 p-6">
                <span className="label text-[10px]">Argyle · Hwy 377</span>
                <p className="mt-2 font-cond text-2xl font-bold uppercase leading-tight text-[#f2ece0]">
                  Pros sit in this chair.
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Complimentary drink on the house, every visit.
                </p>
              </figcaption>
            </m.figure>
          </div>
        </div>
      </div>
    </section>
  );
};
